// src/context/TourContext.js
// Manages the guided tour for new users

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useSettings } from './SettingsContext';
import { useSubscription } from './SubscriptionContext';

const TourContext = createContext();

export function useTour() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}

// Tour steps organized by page/section
const TOUR_STEPS = {
  welcome: [
    {
      target: 'body',
      content: (
        <div className="text-center">
          <h2 className="text-lg font-bold mb-2">Welcome to Nexus!</h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Let's take a quick tour of the essentials to get you started.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
  ],

  // Sidebar navigation links tour
  navlinks: [
    {
      target: '[data-tour="nav-dashboard"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Dashboard</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Your home base. View key metrics, recent activity, and quick actions.
          </p>
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="nav-inventory"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Inventory</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Manage all your products — stock levels, costs, item details. The dropdown also gives you quick access to your <strong>Vault</strong> (archived sold items).
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="nav-sales"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Sales</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Record sales and track your revenue. Inventory updates automatically.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="nav-expenses"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Expenses</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Track business expenses and recurring costs like renewals.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="nav-orders-tracking"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Orders &amp; Tracking</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Auto-import orders from your inbox and track incoming packages — all in one unified view.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="nav-scanner"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">My Products</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Scan barcodes to quickly look up or add products.
          </p>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '[data-tour="nav-reports"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Reports</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Dive into the numbers — revenue trends, profit breakdowns, and inventory analytics.
          </p>
        </div>
      ),
      placement: 'right',
    },
  ],

  dashboard: [
    {
      target: '[data-tour="dashboard-stats"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Your Stats</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            See your key metrics at a glance - inventory value, sales, profits, and more.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="dashboard-actions"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Quick Actions</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Quickly add items, create sales, or perform common tasks from here.
          </p>
        </div>
      ),
      placement: 'bottom',
    },
  ],

  inventory: [
    {
      target: '[data-tour="inventory-add"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Add Items</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Click here to add products to your inventory. You can also scan barcodes!
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="inventory-search"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Search & Filter</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Find items by name, SKU, or use filters to narrow down results.
          </p>
        </div>
      ),
      placement: 'bottom',
    },
  ],

  sales: [
    {
      target: '[data-tour="sales-add"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Record Sales</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            When you sell items, record the sale here. Your inventory updates automatically.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
      disableScrolling: true,
    },
  ],

  // In-page tour on /expenses — mirrors the inventory + sales pattern.
  // Three steps: add an expense, search/filter, manage renewals (the
  // renewals page was retired and now lives as a drawer triggered from
  // the toolbar repeat-icon).
  expenses: [
    {
      target: '[data-tour="expenses-add"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Add Expense</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Log business expenses here — shipping, supplies, subscriptions, anything you spend.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
      disableScrolling: true,
    },
    {
      target: '[data-tour="expenses-search"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Search &amp; Filter</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Find expenses by description, category, or store. Filter by date range, status, or recurring vs one-time.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
      disableScrolling: true,
    },
    {
      target: '[data-tour="expenses-renewals"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Renewals</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Recurring subscriptions and monthly bills. The drawer shows what's due, what's overdue, and lets you mark items paid in bulk.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
      disableScrolling: true,
    },
  ],

  navbar: [
    {
      target: '[data-tour="user-profile"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Your Account</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Access your account settings, preferences, and sign out from here.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
      disableScrolling: true,
    },
    {
      target: '[data-tour="notifications"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Notifications</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Stay updated with alerts, reminders, and important updates.
          </p>
        </div>
      ),
      placement: 'top',
      disableScrolling: true,
    },
  ],

  restartTour: [
    {
      target: '[data-tour="restart-tour"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Restart Tour</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Click this lightbulb anytime to restart the tour and refresh your memory.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
      disableScrolling: true,
    },
  ],

  footer: [
    {
      target: '[data-tour="footer-home"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Welcome Page</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Click here to view the welcome page with an overview of Nexus features.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
      disableScrolling: true,
    },
    {
      target: '[data-tour="footer-discord"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Join Our Discord</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Get support, share feedback, and connect with the Nexus community.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
      disableScrolling: true,
    },
    {
      target: '[data-tour="privacy-toggle"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Hide / Blur Mode</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            One-click blur on every dollar amount in the app — great for streaming, screen-sharing, or working in public.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
      disableScrolling: true,
    },
    {
      target: '[data-tour="theme-toggle"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Light / Dark Mode</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Toggle between light and dark theme. Your choice persists across sessions.
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true,
      disableScrolling: true,
    },
  ],

  // Free-tier only: explains the 25/wk Orders & Tracking quota chip.
  // Plus / Lifetime users skip this section entirely (tierOnly filter).
  freeQuota: [
    {
      target: '[data-tour="orders-tracking-quota"]',
      content: (
        <div>
          <h3 className="font-semibold mb-1">Your Weekly Quota</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            On the Free plan you can add up to <strong>25 orders &amp; packages per week</strong>. This chip shows what you've used — resets every Sunday. Upgrade to <strong>Plus</strong> for unlimited.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
      tierOnly: ['free'],
    },
  ],

  // Two completion variants — Free gets an upgrade nudge, Plus gets a
  // simple "you're set" thank-you. Tour builder picks whichever one
  // matches the current tier via the tierOnly filter; the other is
  // skipped entirely.
  completion: [
    {
      target: 'body',
      content: (
        <div className="text-center py-2">
          <div className="text-3xl mb-3">🎉</div>
          <h2 className="text-lg font-bold mb-2">You're All Set!</h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-2">
            You've completed the tour. Start by adding your first inventory item.
          </p>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Want unlimited orders &amp; packages, plus exports and reports?{' '}
            <a href="/account#billing" className="font-medium text-[rgb(var(--accent-primary))] hover:underline">
              Upgrade to Plus
            </a>
            .
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      disableScrolling: true,
      tierOnly: ['free'],
    },
    {
      target: 'body',
      content: (
        <div className="text-center py-2">
          <div className="text-3xl mb-3">🎉</div>
          <h2 className="text-lg font-bold mb-2">Welcome to Plus!</h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            You've completed the tour. Everything is unlocked — unlimited orders, packages, exports, and reports. Time to get to work.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      disableScrolling: true,
      tierOnly: ['pro'],
    },
  ],
};

// Define the tour flow (order of sections and navigation between them).
//
// Tier-aware: individual steps can carry `tierOnly: ['free']` or
// `tierOnly: ['pro']` and the runtime builder filters them. Sections
// whose every step gets filtered out yield zero steps and effectively
// vanish from the flow.
//
// route: null means "stay on whatever page we're already on". Use null
// for sections whose targets are persistent UI (sidebar nav, Footer,
// body) that exist on every route — saves a navigation, which means
// no page-content swap and no associated "page reload" flash through
// the tour overlay. Only sections that target page-specific content
// (dashboard-stats, inventory-add, sales-add, orders-tracking-quota)
// need to actually navigate.
const TOUR_FLOW = [
  { section: 'welcome', route: null },              // target=body, anywhere works
  { section: 'navlinks', route: null },             // Sidebar — persistent, no nav needed
  { section: 'dashboard', route: '/dashboard' },    // Dashboard content (page-specific)
  { section: 'inventory', route: '/inventory' },    // Inventory page (page-specific)
  { section: 'sales', route: '/sales' },            // Sales page (page-specific)
  { section: 'expenses', route: '/expenses' },      // Expenses + renewals drawer (page-specific)
  { section: 'freeQuota', route: '/orders-tracking' }, // Free-only — chip on /orders-tracking
  { section: 'navbar', route: null },               // user-profile / notifications — persistent
  { section: 'restartTour', route: null },          // lightbulb in sidebar — persistent
  { section: 'footer', route: null },               // Footer (incl. privacy + theme toggles) — persistent
  { section: 'completion', route: null },           // target=body — anywhere
];

// Build all steps for the given tier. Steps with `tierOnly` are kept
// only when the user's tier appears in that list. Steps without
// `tierOnly` always run.
const buildStepsForTier = (tier) => {
  let allSteps = [];
  for (let i = 0; i < TOUR_FLOW.length; i++) {
    const section = TOUR_FLOW[i];
    const sectionSteps = TOUR_STEPS[section.section] || [];
    const filtered = sectionSteps.filter(step =>
      !step.tierOnly || step.tierOnly.includes(tier)
    );
    allSteps = [...allSteps, ...filtered.map(step => ({
      ...step,
      disableBeacon: true, // Disable beacon animation on all steps to prevent stuck animations
      data: { section: section.section, sectionIndex: i, route: section.route },
    }))];
  }
  return allSteps;
};

export function TourProvider({ children }) {
  const { currentUser } = useAuth();
  const { settings, updateSettings, isLoading: settingsLoading } = useSettings();
  const { subscriptionStatus } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  // Tier-aware tour: Free users see the 25/wk quota explanation + an
  // upgrade nudge on completion; Plus / Lifetime skip those and see a
  // "Welcome to Plus" sign-off. Normalize the tier through the same
  // 'pro' vs 'free' bucket the rest of the app uses (lifetime → pro).
  const currentTier = subscriptionStatus?.isPro ? 'pro' : 'free';
  const ALL_STEPS = useMemo(() => buildStepsForTier(currentTier), [currentTier]);

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [tourActive, setTourActive] = useState(false); // Track if tour is active (vs stopped)
  const [tourKey, setTourKey] = useState(0); // Unique key to force remount
  const [tourCompletedThisSession, setTourCompletedThisSession] = useState(false); // Prevent restart after completion
  const [welcomeModalDismissed, setWelcomeModalDismissed] = useState(false); // Tracks if WelcomeModal has been closed

  // Use ref to track intended step during navigation
  const pendingStepRef = React.useRef(null);

  // Called by WelcomeModal when it closes (so tour knows it's safe to start)
  const onWelcomeModalClosed = useCallback(() => {
    setWelcomeModalDismissed(true);
  }, []);

  // Check if WelcomeModal has been shown/dismissed
  const isWelcomeModalDismissed = useCallback(() => {
    if (!currentUser) return true;
    // Use state-based tracking - only true after WelcomeModal explicitly calls onWelcomeModalClosed
    if (welcomeModalDismissed) return true;
    // For returning users who already completed the tour, the modal won't show
    if (settings?.hasCompletedTour === true) return true;
    // Check localStorage as fallback for account age > 5 min (WelcomeModal auto-dismisses)
    const welcomeShown = localStorage.getItem(`nexus_welcome_shown_${currentUser.uid}`);
    if (welcomeShown) {
      // Only trust localStorage if account is old enough (not a new signup where localStorage was just set)
      const accountAge = currentUser.created_at ? Date.now() - new Date(currentUser.created_at).getTime() : Infinity;
      if (accountAge > 5 * 60 * 1000) return true; // Account older than 5 min
    }
    return false;
  }, [currentUser, welcomeModalDismissed, settings?.hasCompletedTour]);

  // Check if user should see the tour (new user or hasn't completed tour)
  // Wait for WelcomeModal to be dismissed first
  useEffect(() => {
    // Only proceed if we have a user and settings are fully loaded
    if (!currentUser || !settings || settingsLoading) return;

    // If tour was completed this session, never restart
    if (tourCompletedThisSession) {
      setIsReady(false);
      return;
    }

    // If user has already completed the tour, never set isReady
    if (settings.hasCompletedTour === true) {
      setIsReady(false);
      return;
    }

    // Don't start if tour is already running
    if (run) return;

    // Check periodically if WelcomeModal has been dismissed
    const checkWelcomeModal = () => {
      if (isWelcomeModalDismissed()) {
        // WelcomeModal is done, start the interactive tour after a brief delay
        setTimeout(() => {
          setIsReady(true);
        }, 500);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkWelcomeModal()) return;

    // If not dismissed yet, poll every 500ms
    const interval = setInterval(() => {
      if (checkWelcomeModal()) {
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [currentUser, settings, settings?.hasCompletedTour, run, isWelcomeModalDismissed, tourCompletedThisSession, settingsLoading]);

  // Start tour when ready
  const startTour = useCallback(() => {
    // Preload the lazy-loaded route chunks the tour will visit so the
    // Suspense fallback (PageLoader spinner) doesn't briefly flash on
    // first navigation to each page. By the time the user clicks
    // Next, the chunks are already in webpack's cache and Suspense
    // resolves synchronously — no visible page-reload flash through
    // the joyride overlay. Identical import path = identical chunk =
    // shared with MainApp's lazy() refs.
    Promise.all([
      import('../pages/Dashboard'),
      import('../pages/InventoryPage'),
      import('../pages/SalesPage'),
      import('../pages/ExpensesPage'),
      import('../pages/OrdersTrackingPage'),
    ]).catch(() => {
      // Preload failures are non-fatal — the lazy fallback handles it.
    });

    setStepIndex(0);
    setTourActive(true);
    setRun(true);
  }, []);

  // Auto-start tour for new users (only after WelcomeModal is dismissed)
  useEffect(() => {
    // Don't auto-start if settings are still loading
    if (settingsLoading) return;

    // Don't auto-start if tour was completed this session
    if (tourCompletedThisSession) return;

    // Don't auto-start if user has already completed the tour
    if (settings?.hasCompletedTour === true) return;

    // Auto-start on / or /dashboard. The MainApp redirects / → /dashboard,
    // so a freshly-landed user is technically on /dashboard before this
    // check ever fires. Accept both so the auto-start gate doesn't miss
    // them.
    const isLandingRoute = location.pathname === '/' || location.pathname === '/dashboard';
    if (isReady && !run && !tourActive && isLandingRoute && isWelcomeModalDismissed()) {
      startTour();
    }
  }, [isReady, run, tourActive, location.pathname, startTour, isWelcomeModalDismissed, settings?.hasCompletedTour, tourCompletedThisSession, settingsLoading]);

  // Cleanup any stuck Joyride elements from the DOM
  const cleanupJoyrideElements = useCallback(() => {
    // Remove any leftover Joyride portals/elements - be very aggressive
    const selectors = [
      '.__floater',
      '.react-joyride',
      '[data-react-joyride]',
      '.joyride-beacon',
      '.joyride-overlay',
      '.joyride-spotlight',
      '.react-joyride__overlay',
      '.react-joyride__spotlight',
      '.react-joyride__beacon',
      '[data-__floater-id]',
      '[id^="react-joyride"]',
    ];

    selectors.forEach(selector => {
      try {
        document.querySelectorAll(selector).forEach(el => {
          try {
            // Use el.remove() which is safer than removeChild
            if (el && el.isConnected) {
              el.remove();
            }
          } catch (removeError) {
            // Ignore individual element removal errors
          }
        });
      } catch (e) {
        // Ignore errors from invalid selectors
      }
    });
  }, []);

  // End tour helper - handles cleanup in correct order
  const endTour = useCallback(() => {
    // Step 1: Stop Joyride from running
    setRun(false);

    // Step 2: After a tick, unmount the component by setting tourActive false
    setTimeout(() => {
      setTourActive(false);
      setStepIndex(0);
      setIsNavigating(false);
      pendingStepRef.current = null;
      // Increment key to ensure fresh mount next time
      setTourKey(k => k + 1);

      // Step 3: Clean up any remaining DOM elements
      setTimeout(cleanupJoyrideElements, 50);
      setTimeout(cleanupJoyrideElements, 150);
      setTimeout(cleanupJoyrideElements, 300);
    }, 50);
  }, [cleanupJoyrideElements]);

  // Skip the tour (user explicitly skips) - exposed for external use
  const skipTour = useCallback(() => {
    // Mark as completed this session FIRST to prevent any restart
    setTourCompletedThisSession(true);

    setRun(false);
    setStepIndex(0);
    setIsNavigating(false);
    pendingStepRef.current = null;

    // Delay unmounting to let Joyride clean itself up
    setTimeout(() => {
      setTourActive(false);
      setTourKey(k => k + 1);
    }, 100);

    // Mark tour as completed so it doesn't show again
    updateSettings({ hasCompletedTour: true }).catch(e => {
      console.warn('[Tour] Could not save tour completion status:', e);
    });
  }, [updateSettings]);

  // Restart tour (from settings or WelcomeModal "Take a Quick Tour" button)
  // skipWelcomeStep: if true, skip the first "Welcome to Nexus!" step (already shown in WelcomeModal)
  const restartTour = useCallback((skipWelcomeStep = false) => {
    // Reset completion flags so the tour can run
    setTourCompletedThisSession(false);
    // Skip past the welcome section step if user already saw the WelcomeModal
    const startIndex = skipWelcomeStep ? (TOUR_STEPS.welcome?.length || 1) : 0;
    setStepIndex(startIndex);
    // Navigate to dashboard first
    navigate('/dashboard');
    // Small delay to let navigation complete
    setTimeout(() => {
      setTourActive(true);
      setRun(true);
    }, 300);
  }, [navigate]);

  // Handle Joyride callback
  const handleJoyrideCallback = useCallback((data) => {
    const { action, index, status, type } = data;

    // Handle tour completion (user clicked "Got it!" on the last step)
    // Also handle when user clicks "Next" on the final step
    if (status === STATUS.FINISHED || (type === EVENTS.STEP_AFTER && index === ALL_STEPS.length - 1)) {
      // Mark as completed this session FIRST to prevent any restart
      setTourCompletedThisSession(true);

      // Step 1: Stop Joyride from running first
      setRun(false);

      // Step 2: Clean up state
      setStepIndex(0);
      setIsNavigating(false);
      pendingStepRef.current = null;

      // Step 3: Delay unmounting the component to let Joyride clean itself up
      setTimeout(() => {
        setTourActive(false);
        setTourKey(k => k + 1);
      }, 100);

      // Save completion to database (fire and forget)
      updateSettings({ hasCompletedTour: true }).catch(e => {
        console.warn('[Tour] Could not save tour completion status:', e);
      });

      // Navigate back to dashboard after cleanup
      setTimeout(() => navigate('/dashboard'), 200);
      return;
    }

    // Handle tour skipped (user clicked "Skip tour")
    if (status === STATUS.SKIPPED) {
      // Mark as completed this session FIRST to prevent any restart
      setTourCompletedThisSession(true);

      setRun(false);
      setStepIndex(0);
      setIsNavigating(false);
      pendingStepRef.current = null;

      setTimeout(() => {
        setTourActive(false);
        setTourKey(k => k + 1);
      }, 100);

      updateSettings({ hasCompletedTour: true }).catch(e => {
        console.warn('[Tour] Could not save tour completion status:', e);
      });
      return;
    }

    // Handle close button
    if (action === ACTIONS.CLOSE) {
      // Mark as completed this session FIRST to prevent any restart
      setTourCompletedThisSession(true);

      pendingStepRef.current = null;
      setRun(false);
      setStepIndex(0);
      setIsNavigating(false);

      setTimeout(() => {
        setTourActive(false);
        setTourKey(k => k + 1);
      }, 100);

      updateSettings({ hasCompletedTour: true }).catch(e => {
        console.warn('[Tour] Could not save tour completion status:', e);
      });
      return;
    }

    // Ignore all other callbacks while navigating
    if (isNavigating) {
      return;
    }

    // IMPORTANT: Ignore callbacks where Joyride's index doesn't match our stepIndex
    // This happens when Joyride internally resets after we toggle run off/on
    if (index !== stepIndex) {
      return;
    }

    // Handle step changes - only on STEP_AFTER with NEXT or PREV actions
    if (type === EVENTS.STEP_AFTER && (action === ACTIONS.NEXT || action === ACTIONS.PREV)) {
      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);

      if (nextIndex >= 0 && nextIndex < ALL_STEPS.length) {
        const nextStep = ALL_STEPS[nextIndex];
        const currentRoute = location.pathname;
        const nextRoute = nextStep?.data?.route;

        // If next step requires a different route, navigate first.
        //
        // Critical: do NOT toggle `run` during the navigation. In
        // react-joyride v2.9, setting run=false hides the overlay +
        // spotlight even if the component itself stays mounted — that
        // was the source of the "screen flash" between steps (overlay
        // disappears, page navigates, overlay reappears).
        //
        // Instead we keep run=true and let joyride hold its current
        // spotlight while the page transitions underneath. After the
        // new page's DOM has had time to render (800ms), we update
        // stepIndex and joyride animates the spotlight to the new
        // target. The overlay stays visible the whole time.
        //
        // isNavigating + the index!==stepIndex guard above prevent
        // joyride callbacks from firing during this window.
        if (nextRoute && nextRoute !== currentRoute) {
          pendingStepRef.current = nextIndex;
          setIsNavigating(true);
          navigate(nextRoute);
          setTimeout(() => {
            const targetStep = pendingStepRef.current;
            if (targetStep !== null) {
              setStepIndex(targetStep);
              pendingStepRef.current = null;
            }
            setIsNavigating(false);
          }, 800);
        } else {
          setStepIndex(nextIndex);
        }
      }
    }
  }, [isNavigating, stepIndex, location.pathname, navigate, updateSettings, ALL_STEPS]);

  // Custom styles for the tour
  const tourStyles = {
    options: {
      arrowColor: 'var(--color-card)',
      backgroundColor: 'var(--color-card)',
      overlayColor: 'rgba(0, 0, 0, 0.6)',
      primaryColor: 'rgb(var(--accent-primary))',
      textColor: 'var(--color-text)',
      zIndex: 10000,
    },
    tooltip: {
      borderRadius: '12px',
      padding: '16px',
    },
    tooltipContainer: {
      textAlign: 'left',
    },
    tooltipTitle: {
      fontSize: '16px',
      fontWeight: 600,
    },
    tooltipContent: {
      padding: '8px 0',
    },
    buttonNext: {
      backgroundColor: 'rgb(var(--accent-primary))',
      borderRadius: '8px',
      color: '#fff',
      fontSize: '13px',
      fontWeight: 500,
      padding: '8px 16px',
    },
    buttonBack: {
      color: 'var(--color-text-muted)',
      fontSize: '13px',
      fontWeight: 500,
      marginRight: '8px',
    },
    buttonSkip: {
      color: 'var(--color-text-muted)',
      fontSize: '13px',
    },
    buttonClose: {
      color: 'var(--color-text-muted)',
    },
    spotlight: {
      borderRadius: '8px',
    },
  };

  // Expose the section + target of the currently-active step so
  // consumers (e.g. the Navbar opening its Inventory dropdown for the
  // matching step) can react without needing to know exact step
  // indices. Indices shift whenever steps are added/removed; section
  // and target string are stable.
  const activeStep = (tourActive && run) ? ALL_STEPS[stepIndex] : null;
  const currentStepSection = activeStep?.data?.section || null;
  const currentStepTarget = activeStep?.target || null;

  const value = {
    run,
    startTour,
    skipTour,
    restartTour,
    onWelcomeModalClosed,
    isReady,
    currentStep: stepIndex,
    currentStepSection,
    currentStepTarget,
    tourActive,
    totalSteps: ALL_STEPS.length,
  };

  // Expose tour controls to window for testing (dev only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' || true) { // Always expose for now
      window.__nexusTour = {
        start: startTour,
        restart: restartTour,
        skip: skipTour,
        getState: () => ({ run, stepIndex, isReady, isNavigating }),
      };
    }
    return () => {
      delete window.__nexusTour;
    };
  }, [startTour, restartTour, skipTour, run, stepIndex, isReady, isNavigating]);

  return (
    <TourContext.Provider value={value}>
      {children}
      {/* Keep Joyride mounted whenever the tour is active. The `run`
          prop controls play/pause INTERNALLY — toggling it doesn't
          remount the component, so the overlay + spotlight don't
          disappear during route changes / step transitions. This is
          what eliminates the screen-flash + "sidebar dims for a frame"
          that happens when the whole component unmounts. The aggressive
          DOM cleanup in cleanupJoyrideElements still runs on real end
          (skipTour / FINISHED / CLOSE), so no leftover portals. */}
      {tourActive && (
        <Joyride
          key={`tour-${tourKey}`}
          steps={ALL_STEPS}
          run={run}
          stepIndex={stepIndex}
          continuous
          showProgress
          showSkipButton
          hideCloseButton={false}
          disableOverlayClose
          // Stop joyride from scrolling on every step — scroll-to-step
          // causes a brief repaint that reads as a flash on steps that
          // are already in view. The targets used in this tour are all
          // above-the-fold for typical viewports, so scroll-to isn't
          // helpful.
          disableScrolling
          disableCloseOnEsc={false}
          scrollToFirstStep={false}
          spotlightClicks={false}
          disableBeacon
          callback={handleJoyrideCallback}
          styles={tourStyles}
          locale={{
            back: 'Back',
            close: 'Close',
            last: 'Got it!',
            next: 'Next',
            skip: 'Skip tour',
          }}
          floaterProps={{
            // Let floater animate between targets — masks the position
            // recalculation that otherwise reads as a flicker.
            disableAnimation: false,
            hideArrow: false,
          }}
        />
      )}
    </TourContext.Provider>
  );
}

export default TourContext;
